import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Plus, X } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { useDropdowns } from "@/hooks/useMeta";
import { ROLE_LABELS } from "@/lib/constants";
import { PageHeader } from "@/components/PageHeader";
import { DivisionTag } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { MenuManager } from "@/components/MenuManager";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";

const PREFS = [
  ["lead_assignment", "Lead assigned to me"],
  ["deal_stage", "Deal stage changes"],
  ["quote_approval", "Quote approval requests & results"],
  ["followup_overdue", "Follow-up overdue reminders"],
  ["large_deal", "Large deal alerts (> 0.50 Cr)"],
  ["deal_overdue", "Deal past expected close date"],
  ["dealer_lead", "New lead submitted by dealer"],
];

function DropdownEditor({ label, dkey, options = [], onSaved }) {
  const [items, setItems] = useState(options);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setItems(options); }, [options]);

  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (items.includes(v)) { toast.error("Option already exists"); return; }
    setItems([...items, v]);
    setDraft("");
  };
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!items.length) { toast.error("Add at least one option"); return; }
    setSaving(true);
    try {
      await api.put(`/settings/dropdowns/${dkey}`, { options: items });
      toast.success(`${label} updated`);
      onSaved?.();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-2" data-testid={`dropdown-editor-${dkey}`}>
      <Label className="text-xs font-semibold text-slate-700">{label}</Label>
      <div className="flex flex-wrap gap-2">
        {items.map((it, i) => (
          <span key={it} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 text-sm rounded-full pl-3 pr-1.5 py-1" data-testid={`option-${dkey}-${it}`}>
            {it}
            <button onClick={() => remove(i)} className="rounded-full hover:bg-slate-300/60 p-0.5" data-testid={`remove-${dkey}-${it}`}><X className="h-3 w-3" /></button>
          </span>
        ))}
        {items.length === 0 && <span className="text-xs text-slate-400">No options yet</span>}
      </div>
      <div className="flex gap-2 max-w-sm">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder={`Add ${label.toLowerCase()}…`} className="h-9" data-testid={`add-input-${dkey}`} />
        <Button type="button" variant="outline" size="sm" className="gap-1" onClick={add} data-testid={`add-btn-${dkey}`}><Plus className="h-4 w-4" /> Add</Button>
      </div>
      <Button size="sm" onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 gap-2 mt-1" data-testid={`save-${dkey}`}>{saving && <Loader2 className="h-4 w-4 animate-spin" />}Save {label}</Button>
    </div>
  );
}

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const qc = useQueryClient();
  const { data: dd } = useDropdowns();
  const [prefs, setPrefs] = useState(user?.notification_preferences || {});
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [profile, setProfile] = useState({ name: user?.name || "", designation: user?.designation || "", phone: user?.phone || "" });
  const [savingProfile, setSavingProfile] = useState(false);

  const canManageOptions = ["super_admin", "sales_head", "sales_manager"].includes(user?.role);

  const togglePref = (k) => setPrefs((p) => ({ ...p, [k]: !p[k] }));

  const savePrefs = async () => {
    setSavingPrefs(true);
    try { await api.put("/auth/preferences", { notification_preferences: prefs }); await refreshUser(); toast.success("Preferences saved"); }
    catch (e) { toast.error(apiError(e)); } finally { setSavingPrefs(false); }
  };

  const saveProfile = async () => {
    if (!profile.name.trim()) { toast.error("Name is required"); return; }
    setSavingProfile(true);
    try { await api.put("/auth/profile", profile); await refreshUser(); toast.success("Profile updated"); }
    catch (e) { toast.error(apiError(e)); } finally { setSavingProfile(false); }
  };

  const changePw = async () => {
    if (!pw.current_password || !pw.new_password) { toast.error("Fill both fields"); return; }
    setSavingPw(true);
    try { await api.post("/auth/change-password", pw); setPw({ current_password: "", new_password: "" }); toast.success("Password changed"); }
    catch (e) { toast.error(apiError(e)); } finally { setSavingPw(false); }
  };

  return (
    <div className="max-w-3xl">
      <PageHeader title="Settings" subtitle="Manage your profile, notifications and password." />

      <Card className="p-5 mb-6" data-testid="profile-card">
        <h3 className="font-heading font-semibold text-sm text-slate-800 mb-1">Profile</h3>
        <p className="text-xs text-slate-500 mb-4">Update your name, designation and phone. Email, role and division are managed by your administrator.</p>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label className="text-xs">Name</Label><Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="h-9" data-testid="profile-name-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Designation / Title</Label><Input value={profile.designation} onChange={(e) => setProfile({ ...profile, designation: e.target.value })} placeholder="e.g. Sales Manager — West" className="h-9" data-testid="profile-designation-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Phone</Label><Input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className="h-9" data-testid="profile-phone-input" /></div>
          <div><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Email</p><p className="text-sm mt-0.5">{user?.email}</p></div>
          <div><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Role</p><p className="text-sm mt-0.5">{ROLE_LABELS[user?.role]}</p></div>
          <div><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Division</p><div className="mt-1">{user?.division_id ? <DivisionTag id={user.division_id} /> : <span className="text-sm">All Divisions</span>}</div></div>
        </div>
        <Button onClick={saveProfile} disabled={savingProfile} className="mt-4 bg-slate-900 hover:bg-slate-800 gap-2" data-testid="save-profile-btn">{savingProfile && <Loader2 className="h-4 w-4 animate-spin" />}Save Profile</Button>
      </Card>

      {canManageOptions && (
        <Card className="p-5 mb-6" data-testid="sample-options-card">
          <h3 className="font-heading font-semibold text-sm text-slate-800 mb-1">Sample Options</h3>
          <p className="text-xs text-slate-500 mb-4">Customize the Sample Type and Sample Status dropdowns used across the Samples module.</p>
          <div className="grid sm:grid-cols-2 gap-6">
            <DropdownEditor label="Sample Type" dkey="sample_type" options={dd?.sample_type || []} onSaved={() => qc.invalidateQueries({ queryKey: ["dropdowns"] })} />
            <DropdownEditor label="Sample Status" dkey="sample_status" options={dd?.sample_status || []} onSaved={() => qc.invalidateQueries({ queryKey: ["dropdowns"] })} />
          </div>
        </Card>
      )}

      {user?.role === "super_admin" && (
        <Card className="p-5 mb-6" data-testid="winloss-reasons-card">
          <h3 className="font-heading font-semibold text-sm text-slate-800 mb-1">Win / Loss Reasons</h3>
          <p className="text-xs text-slate-500 mb-4">Customize the reasons offered when a lead or opportunity is marked Won or Lost. These power the BI Win/Loss analysis.</p>
          <div className="grid sm:grid-cols-2 gap-6">
            <DropdownEditor label="Won Reasons" dkey="won_reason" options={dd?.won_reason || []} onSaved={() => qc.invalidateQueries({ queryKey: ["dropdowns"] })} />
            <DropdownEditor label="Lost Reasons" dkey="lost_reason" options={dd?.lost_reason || []} onSaved={() => qc.invalidateQueries({ queryKey: ["dropdowns"] })} />
          </div>
        </Card>
      )}

      {user?.role === "super_admin" && (
        <Card className="p-5 mb-6" data-testid="menu-manager-card">
          <h3 className="font-heading font-semibold text-sm text-slate-800 mb-1">Menu Manager</h3>
          <p className="text-xs text-slate-500 mb-4">Control which sidebar menu items each role can see.</p>
          <MenuManager />
        </Card>
      )}

      <Card className="p-5 mb-6">
        <h3 className="font-heading font-semibold text-sm text-slate-800 mb-1">Notification Preferences</h3>
        <p className="text-xs text-slate-500 mb-4">Choose which WhatsApp & in-app alerts you receive.</p>
        <div className="space-y-3">
          {PREFS.map(([k, label]) => (
            <div key={k} className="flex items-center justify-between py-1">
              <span className="text-sm text-slate-700">{label}</span>
              <Switch checked={!!prefs[k]} onCheckedChange={() => togglePref(k)} data-testid={`pref-${k}`} />
            </div>
          ))}
        </div>
        <Button onClick={savePrefs} disabled={savingPrefs} className="mt-4 bg-slate-900 hover:bg-slate-800 gap-2" data-testid="save-prefs-btn">{savingPrefs && <Loader2 className="h-4 w-4 animate-spin" />}Save Preferences</Button>
      </Card>

      <Card className="p-5">
        <h3 className="font-heading font-semibold text-sm text-slate-800 mb-4">Change Password</h3>
        <div className="grid grid-cols-2 gap-4 max-w-md">
          <div className="space-y-1.5"><Label className="text-xs">Current Password</Label><PasswordInput value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="h-9" testid="current-password" /></div>
          <div className="space-y-1.5"><Label className="text-xs">New Password</Label><PasswordInput value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="h-9" testid="new-password" /></div>
        </div>
        <Button onClick={changePw} disabled={savingPw} variant="outline" className="mt-4 gap-2" data-testid="change-password-btn">{savingPw && <Loader2 className="h-4 w-4 animate-spin" />}Update Password</Button>
      </Card>
    </div>
  );
}
